// src/components/RevenueReportScreen.jsx
// Relatório de receitas premium.
// Rota real: /finance/revenue/report
// Endpoint: GET /psy/finance/revenue?id=ID_USUARIO
// UI isolada em .rev-* para não mexer em .dash-* global.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import AppSidebar from "./AppSidebar";
import Logo from "../assets/logoClaro.webp";
import "../sass/pages/revenue-report.scss";

import { API_BASE as CONFIG_API_BASE } from "../config/apiBase";

const API_BASE = (
  CONFIG_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

const RECEIPT_FILTERS = [
  { value: "todas", label: "Todas" },
  { value: "consultas", label: "Consultas" },
  { value: "outros", label: "Outros" },
];

function readStorage(keys = []) {
  for (const key of keys) {
    const value = sessionStorage.getItem(key) || localStorage.getItem(key);

    if (value && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
}

function saveAuthToken(token) {
  if (!token) return;

  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
}

function normalizeText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function safeNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function moneyBR(value) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(safeNumber(value));
}

function percentBR(value) {
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(safeNumber(value));
}

function parseDate(value) {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateBR(value) {
  const date = parseDate(value);

  if (!date) return "Não informado";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function getTimeBR(value) {
  const date = parseDate(value);

  if (!date) return "--:--";

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();

  if (!clean) return "";

  return clean.split(/\s+/)[0];
}

function getMonthLabel(value, index = 0) {
  const month = Number(value);

  if (Number.isFinite(month)) {
    if (month >= 1 && month <= 12) return MONTHS[month - 1];
    if (month >= 0 && month <= 11) return MONTHS[month];
  }

  return MONTHS[index % 12];
}

function getRevenueCategory(item) {
  return String(item?.categoria || item?.servicoId || item?.category || "Receita").trim() || "Receita";
}

function getRevenueValue(item) {
  return safeNumber(item?.valor ?? item?.valorTotal ?? item?.total ?? item?.value ?? 0);
}

function getRevenueDate(item) {
  return item?.data || item?.dataCadastro || item?.createdAt || item?.vencimento || null;
}

function getRevenueDescription(item) {
  return String(item?.descricao || item?.description || getRevenueCategory(item) || "Receita").trim();
}

function isConsultaCategory(category = "") {
  const normalized = normalizeText(category);

  return (
    normalized.includes("consulta") ||
    normalized.includes("sessao") ||
    normalized.includes("atendimento")
  );
}

function normalizeEvolution(data = []) {
  const rows = Array.isArray(data) ? data : [];

  return rows
    .map((item, index) => {
      const valor = safeNumber(item?.valor ?? item?.total ?? item?.receita ?? 0);
      const mes = item?.mes ?? item?.month ?? index;

      return {
        label: item?.label || item?.nome || getMonthLabel(mes, index),
        valor,
      };
    })
    .filter((item) => Number.isFinite(item.valor));
}

function normalizeLastRevenues(data = []) {
  const rows = Array.isArray(data) ? data : [];

  return rows.map((item, index) => ({
    id: item?._id || item?.id || `${item?.data || "receita"}-${index}`,
    categoria: getRevenueCategory(item),
    descricao: getRevenueDescription(item),
    valor: getRevenueValue(item),
    data: getRevenueDate(item),
  }));
}

function buildRevenueSearchText(item) {
  return normalizeText(
    [
      item.categoria,
      item.descricao,
      moneyBR(item.valor),
      formatDateBR(item.data),
    ]
      .filter(Boolean)
      .join(" ")
  );
}

async function readApiError(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const payload = await response.json();
      return payload?.message || payload?.error || JSON.stringify(payload);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function getRevenueHealth(total) {
  const value = safeNumber(total);

  if (value <= 0) {
    return {
      label: "Sem receita",
      tone: "neutral",
      icon: "i",
      description: "Nenhuma entrada registrada no período.",
    };
  }

  if (value < 1000) {
    return {
      label: "Em construção",
      tone: "warning",
      icon: "!",
      description: "Há receita registrada, mas ainda em volume baixo.",
    };
  }

  if (value < 5000) {
    return {
      label: "Estável",
      tone: "good",
      icon: "✓",
      description: "Receita com boa previsibilidade para o consultório.",
    };
  }

  return {
    label: "Excelente",
    tone: "great",
    icon: "✓",
    description: "Volume de receita forte no período analisado.",
  };
}

function SummaryCard({ icon, title, value, description, tone = "green" }) {
  return (
    <article className={`rev-sum rev-sum--${tone}`}>
      <div className="rev-sum__ico" aria-hidden="true">
        {icon}
      </div>

      <div className="rev-sum__mid">
        <span>{title}</span>
        <strong>{value}</strong>
        <small>{description}</small>
      </div>
    </article>
  );
}

function DonutChart({ consultas, outros }) {
  const total = safeNumber(consultas) + safeNumber(outros);
  const consultasPercent = total > 0 ? (safeNumber(consultas) / total) * 100 : 0;
  const outrosPercent = total > 0 ? (safeNumber(outros) / total) * 100 : 0;

  return (
    <div className="rev-donut-box">
      <div className="rev-donut" role="img" aria-label="Distribuição de receitas">
        <div
          className="rev-donut__ring"
          style={{
            background: total
              ? `conic-gradient(var(--rev-green-500) 0 ${consultasPercent}%, var(--rev-amber-500) ${consultasPercent}% 100%)`
              : "conic-gradient(var(--rev-slate-200) 0 100%)",
          }}
        />

        <div className="rev-donut__hole">
          <strong>{moneyBR(total)}</strong>
          <span>Total distribuído</span>
        </div>
      </div>

      <div className="rev-legend">
        <LegendRow
          color="green"
          label="Consultas"
          value={moneyBR(consultas)}
          percent={`${percentBR(consultasPercent)}%`}
        />

        <LegendRow
          color="amber"
          label="Outros"
          value={moneyBR(outros)}
          percent={`${percentBR(outrosPercent)}%`}
        />
      </div>
    </div>
  );
}

function LegendRow({ color, label, value, percent }) {
  return (
    <div className="rev-legend__row">
      <span className={`rev-dot rev-dot--${color}`} aria-hidden="true" />
      <div>
        <strong>{label}</strong>
        <small>{percent}</small>
      </div>
      <span>{value}</span>
    </div>
  );
}

function EvolutionChart({ data = [] }) {
  const rows = normalizeEvolution(data);

  if (!rows.length) {
    return (
      <div className="rev-chart-empty">
        <span>📈</span>
        <strong>Sem evolução de receitas.</strong>
        <small>Quando houver dados mensais, o gráfico aparecerá aqui.</small>
      </div>
    );
  }

  const visibleRows = rows.slice(0, 12);
  const maxValue = Math.max(...visibleRows.map((item) => item.valor), 1);
  const maxY = maxValue * 1.18;

  const width = 100;
  const height = 46;
  const left = 5;
  const right = 96;
  const top = 7;
  const base = 39;
  const chartWidth = right - left;
  const gap = 1.35;
  const barWidth = Math.max(2, (chartWidth - gap * (visibleRows.length - 1)) / visibleRows.length);

  const points = visibleRows.map((item, index) => {
    const x = left + index * (barWidth + gap) + barWidth / 2;
    const y = base - (Math.max(0, item.valor) / maxY) * (base - top);
    return `${x},${y}`;
  });

  return (
    <div className="rev-chart">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="rev-svg"
        role="img"
        aria-label="Evolução das receitas"
        preserveAspectRatio="none"
      >
        {[12, 20, 28, 36].map((y) => (
          <line key={y} x1="0" y1={y} x2="100" y2={y} className="rev-grid-line" />
        ))}

        <path d={`M ${left} ${base} L ${right} ${base}`} className="rev-axis" />

        {visibleRows.map((item, index) => {
          const value = Math.max(0, item.valor);
          const barHeight = (value / maxY) * (base - top);
          const x = left + index * (barWidth + gap);
          const y = base - barHeight;

          return (
            <rect
              key={`${item.label}-${index}`}
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              rx="1.2"
              className="rev-bar"
            >
              <title>
                {item.label}: {moneyBR(item.valor)}
              </title>
            </rect>
          );
        })}

        {points.length > 1 ? (
          <polyline points={points.join(" ")} className="rev-line-path" />
        ) : null}

        {points.map((point, index) => {
          const [x, y] = point.split(",").map(Number);

          return (
            <circle
              key={`${point}-${index}`}
              cx={x}
              cy={y}
              r="1.35"
              className="rev-line-dot"
            />
          );
        })}
      </svg>

      <div className="rev-xlabels">
        {visibleRows.map((item, index) => (
          <span key={`${item.label}-${index}`}>{item.label}</span>
        ))}
      </div>
    </div>
  );
}

function RevenueSkeleton() {
  return (
    <div className="rev-skeleton" aria-hidden="true">
      <div className="rev-skeleton__hero" />
      <div className="rev-skeleton__cards">
        <span />
        <span />
        <span />
      </div>
      <div className="rev-skeleton__grid">
        <span />
        <span />
      </div>
      <div className="rev-skeleton__list">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function EmptyState({ title, message, onRetry, onBack, isError = false }) {
  return (
    <section className={`rev-state ${isError ? "rev-state--error" : ""}`}>
      <div className="rev-state__icon">{isError ? "!" : "📈"}</div>
      <strong>{title}</strong>
      <span>{message}</span>

      <div className="rev-state__actions">
        <button className="rev-btn rev-btn--ghost-dark" type="button" onClick={onBack}>
          Voltar
        </button>

        <button className="rev-btn rev-btn--primary" type="button" onClick={onRetry}>
          Tentar novamente
        </button>
      </div>
    </section>
  );
}

export default function RevenueReportScreen() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const [filter, setFilter] = useState("todas");
  const [search, setSearch] = useState("");

  const nomeUsuario = useMemo(() => readStorage([AUTH_KEYS.USER_NAME]) || "", []);
  const firstName = useMemo(() => getFirstName(nomeUsuario), [nomeUsuario]);

  const authOrRedirect = useCallback(() => {
    const token = readStorage([AUTH_KEYS.TOKEN, "token"]);
    const userId = readStorage([AUTH_KEYS.USER_ID, "id"]);

    if (!token || !userId) {
      setError("Sessão expirada. Faça login novamente.");
      return null;
    }

    return { token, userId };
  }, []);

  const loadData = useCallback(
    async ({ silent = false } = {}) => {
      const auth = authOrRedirect();

      if (!auth) {
        setLoading(false);
        return;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const response = await fetch(
          `${API_BASE}/psy/finance/revenue?id=${encodeURIComponent(auth.userId)}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${auth.token}`,
              "Content-Type": "application/json",
            },
          }
        );

        saveAuthToken(response.headers.get("x-new-token"));

        if (response.status === 401 || response.status === 403) {
          setError("Sessão expirada. Faça login novamente.");
          setData(null);
          return;
        }

        if (!response.ok) {
          const message = await readApiError(response);
          setError(message || `Erro ao buscar relatório (${response.status}).`);
          setData(null);
          return;
        }

        const payload = await response.json();
        setData(payload || null);
        setLastUpdatedAt(new Date());
      } catch (err) {
        console.error("Erro ao carregar relatório de receitas:", err);
        setError("Falha de comunicação com a API.");
        setData(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authOrRedirect]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resumo = useMemo(() => {
    const total = safeNumber(data?.total ?? data?.totalReceita ?? 0);
    const consultas = safeNumber(data?.consultas ?? data?.consulta ?? 0);
    const outros = safeNumber(data?.outros ?? 0);

    return { total, consultas, outros };
  }, [data]);

  const distribution = useMemo(() => {
    const totalDistribuido = resumo.consultas + resumo.outros;

    return {
      total: totalDistribuido,
      consultasPercent: totalDistribuido > 0 ? (resumo.consultas / totalDistribuido) * 100 : 0,
      outrosPercent: totalDistribuido > 0 ? (resumo.outros / totalDistribuido) * 100 : 0,
    };
  }, [resumo]);

  const evolution = useMemo(
    () => normalizeEvolution(data?.evolucaoMensal || data?.monthlyRevenue || []),
    [data]
  );

  const revenues = useMemo(
    () => normalizeLastRevenues(data?.ultimasReceitas || data?.revenues || data?.items || []),
    [data]
  );

  const filteredRevenues = useMemo(() => {
    const query = normalizeText(search);

    return revenues.filter((revenue) => {
      const isConsulta = isConsultaCategory(revenue.categoria);
      const matchesFilter =
        filter === "todas" ||
        (filter === "consultas" && isConsulta) ||
        (filter === "outros" && !isConsulta);

      const matchesSearch = !query || buildRevenueSearchText(revenue).includes(query);

      return matchesFilter && matchesSearch;
    });
  }, [revenues, filter, search]);

  const filteredTotals = useMemo(() => {
    const total = filteredRevenues.reduce(
      (sum, revenue) => sum + safeNumber(revenue.valor),
      0
    );

    return {
      total,
      count: filteredRevenues.length,
    };
  }, [filteredRevenues]);

  const averageRevenue = useMemo(() => {
    if (!revenues.length) return 0;

    return revenues.reduce((sum, revenue) => sum + safeNumber(revenue.valor), 0) / revenues.length;
  }, [revenues]);

  const health = useMemo(() => getRevenueHealth(resumo.total), [resumo.total]);

  const biggestCategory = useMemo(() => {
    if (distribution.total <= 0) return "Sem dados";
    return resumo.consultas >= resumo.outros ? "Consultas" : "Outros";
  }, [distribution.total, resumo.consultas, resumo.outros]);

  const reload = () => loadData({ silent: true });
  const goBack = () => navigate("/reports");
  const goFinance = () => navigate("/finance");
  const goLogin = () => navigate("/login");
  const printReport = () => window.print();

  return (
    <div className="rev-page">
      <div className="rev-bg" aria-hidden="true">
        <span className="rev-bg__orb rev-bg__orb--one" />
        <span className="rev-bg__orb rev-bg__orb--two" />
        <span className="rev-bg__orb rev-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="rev-topbar">
        <button
          className="rev-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          ☰
        </button>

        <div className="rev-brand">
          <img src={Logo} alt="PsyAssist" className="rev-brand__logo" />
          <div className="rev-brand__text">
            <strong>Relatório de receitas</strong>
            <span>Financeiro • Visão consolidada • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="rev-top-actions">
          <button
            className="rev-btn rev-btn--ghost rev-btn--desktop"
            type="button"
            onClick={goBack}
          >
            Voltar
          </button>

          <button
            className="rev-btn rev-btn--ghost"
            type="button"
            onClick={printReport}
            disabled={loading || Boolean(error) || !data}
          >
            Imprimir
          </button>

          <button
            className="rev-btn rev-btn--primary"
            type="button"
            onClick={reload}
            disabled={loading || refreshing}
          >
            {refreshing ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
      </header>

      <main className="rev-main">
        {loading ? (
          <RevenueSkeleton />
        ) : error ? (
          <EmptyState
            title="Não foi possível carregar o relatório."
            message={error}
            onRetry={String(error).includes("Sessão") ? goLogin : reload}
            onBack={goBack}
            isError
          />
        ) : !data ? (
          <EmptyState
            title="Nenhum dado encontrado."
            message="Ainda não há receitas suficientes para montar este relatório."
            onRetry={reload}
            onBack={goBack}
          />
        ) : (
          <>
            <section className="rev-hero">
              <div>
                <span className="rev-kicker">Visão de entradas</span>
                <h1>Receitas</h1>
                <p>
                  {firstName ? `${firstName}, ` : ""}
                  acompanhe ganhos por categoria, evolução mensal e últimos
                  recebimentos do consultório.
                </p>
              </div>

              <aside className={`rev-hero-card rev-hero-card--${health.tone}`}>
                <div className="rev-hero-card__icon" aria-hidden="true">
                  {health.icon}
                </div>

                <div>
                  <small>Status do período</small>
                  <strong>{health.label}</strong>
                  <span>{health.description}</span>
                </div>
              </aside>
            </section>

            <section className="rev-grid3">
              <SummaryCard
                title="Total"
                value={moneyBR(resumo.total)}
                icon="💰"
                description="Receita total registrada"
                tone="green"
              />

              <SummaryCard
                title="Consultas"
                value={moneyBR(resumo.consultas)}
                icon="🩺"
                description={`${percentBR(distribution.consultasPercent)}% da distribuição`}
                tone="blue"
              />

              <SummaryCard
                title="Outros"
                value={moneyBR(resumo.outros)}
                icon="📦"
                description={`${percentBR(distribution.outrosPercent)}% da distribuição`}
                tone="amber"
              />
            </section>

            <section className="rev-grid2">
              <div className="rev-panel">
                <div className="rev-panel__head">
                  <div>
                    <span>Distribuição</span>
                    <h2>Distribuição de receitas</h2>
                    <p>Compare quanto veio de consultas e outros lançamentos.</p>
                  </div>
                </div>

                <DonutChart consultas={resumo.consultas} outros={resumo.outros} />

                <div className="rev-insight">
                  <span>Maior origem</span>
                  <strong>{biggestCategory}</strong>
                  <p>
                    Média dos últimos recebimentos: <b>{moneyBR(averageRevenue)}</b>.
                  </p>
                </div>
              </div>

              <div className="rev-panel">
                <div className="rev-panel__head">
                  <div>
                    <span>Evolução</span>
                    <h2>Evolução das receitas</h2>
                    <p>Série mensal das entradas registradas.</p>
                  </div>

                  {lastUpdatedAt ? (
                    <div className="rev-updated">
                      Atualizado às {getTimeBR(lastUpdatedAt)}
                    </div>
                  ) : null}
                </div>

                <div className="rev-chart-card">
                  <EvolutionChart data={evolution} />
                </div>
              </div>
            </section>

            <section className="rev-panel">
              <div className="rev-panel__head rev-panel__head--with-tools">
                <div>
                  <span>Histórico</span>
                  <h2>Últimos recebimentos</h2>
                  <p>Filtre e consulte as receitas recentes do consultório.</p>
                </div>

                <div className="rev-counter">
                  {filteredTotals.count} de {revenues.length}
                </div>
              </div>

              <div className="rev-toolbar">
                <label className="rev-search">
                  <span aria-hidden="true">⌕</span>
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Buscar recebimento..."
                  />

                  {search ? (
                    <button type="button" onClick={() => setSearch("")} aria-label="Limpar busca">
                      ×
                    </button>
                  ) : null}
                </label>

                <div className="rev-filter-group" aria-label="Filtro de receitas">
                  {RECEIPT_FILTERS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={filter === option.value ? "is-active" : ""}
                      onClick={() => setFilter(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <div className="rev-total-chip">
                  <span>Total filtrado</span>
                  <strong>{moneyBR(filteredTotals.total)}</strong>
                </div>
              </div>

              {filteredRevenues.length === 0 ? (
                <div className="rev-empty">
                  Nenhum recebimento encontrado com os filtros atuais.
                </div>
              ) : (
                <div className="rev-list">
                  {filteredRevenues.map((revenue) => {
                    const isConsulta = isConsultaCategory(revenue.categoria);

                    return (
                      <article className="rev-item" key={revenue.id}>
                        <div className={`rev-item__ico ${isConsulta ? "is-consulta" : "is-outro"}`} aria-hidden="true">
                          {isConsulta ? "🩺" : "💵"}
                        </div>

                        <div className="rev-item__txt">
                          <strong>{revenue.descricao || revenue.categoria || "Receita"}</strong>
                          <span>
                            {formatDateBR(revenue.data)} • {revenue.categoria || "Sem categoria"}
                          </span>
                        </div>

                        <div className="rev-item__right">{moneyBR(revenue.valor)}</div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="rev-bottom-actions">
              <button className="rev-btn rev-btn--ghost-dark" type="button" onClick={goBack}>
                Voltar aos relatórios
              </button>

              <button className="rev-btn rev-btn--primary" type="button" onClick={goFinance}>
                Abrir financeiro
              </button>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
